import React, { KeyboardEvent, useCallback, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';

import { queryStrings } from '@trqk/resolvers';
import {
    Button,
    ValidatedInput,
    LoadingEllipses,
    StickyTab,
} from '@trqk/components';
import { encodeURLParams, locale, getWebpackEnvVar } from '@trqk/utils';
import { validateInputs } from '@trqk/hooks';

import { useModal, useToast } from '../../../../../hooks';
import { RequestInvitation } from '../../../../modals';
import { PATHS, URL_PARAMS } from '../../../../../constants';
import { inputIds } from './constants';

const { CHECK_INVITATION_LIST } = queryStrings;

/**
 * @typedef {RequestInvitationTabProps}
 * @property {string} statementId the ID of the statement that was uploaded through the tryout page
 */
interface RequestInvitationTabProps {
    statementId: string;
}

/**
 * A StickyTab that opens a modal which prompts the user to request an invitation to a free trial.
 * @param {RequestInvitationTabProps} props
 */
export const RequestInvitationTab = ({
    statementId,
}: RequestInvitationTabProps): JSX.Element => {
    const invitationListUrl = getWebpackEnvVar('web', 'INVITATION_LIST_URL');
    const modal = useModal();
    const toast = useToast();
    const [cardIsOpen, setCardIsOpen] = useState(true);
    const [email, setEmail] = useState('');
    const [validationOutputs, setValidationOutputs] = useState<
        ValidationOutput[]
    >([]);
    const history = useHistory();

    const validate = useCallback(
        () =>
            validateInputs([
                {
                    inputId: inputIds.requestInvitationEmail,
                    label: 'Email',
                    type: 'email',
                    value: email,
                    required: true,
                },
            ]),
        [email],
    );

    const handleClose = (): void => {
        setEmail('');
    };

    const [checkInvitationList, { loading }] = useLazyQuery(
        CHECK_INVITATION_LIST,
        {
            fetchPolicy: 'network-only',
            onCompleted: data => {
                const hasInvitation =
                    data?.checkInvitationList?.hasInvitation ?? false;

                if (hasInvitation) {
                    toast.add({
                        kind: 'info-banner',
                        content:
                            locale.enUS.STATEMENTS_PREVIEW
                                .REQUEST_INVITATION_TAB.INVITATION_FOUND,
                    });
                    history.push(
                        `${PATHS.SIGN_UP}?${encodeURLParams({
                            [URL_PARAMS.EMAIL]: email,
                            [URL_PARAMS.STATEMENT_ID]: statementId,
                        })}`,
                    );
                } else {
                    modal.add({
                        Content: RequestInvitation,
                        input: {
                            email,
                            statementId,
                        },
                        handleClose,
                    });
                }
            },
            onError: () => {
                toast.add({
                    kind: 'warning-banner',
                    content: locale.enUS.DEFAULT_API_ERROR,
                });
            },
        },
    );

    const handleSubmit = (): void => {
        const validationResults = validate();

        if (!validationResults.hasValidationErrors) {
            setValidationOutputs([]);
            checkInvitationList({
                variables: {
                    email,
                    invitationListUrl,
                },
            });
        } else {
            setValidationOutputs(validationResults.validationOutputs);
        }
    };

    return (
        <StickyTab cardIsOpen={cardIsOpen} setCardIsOpen={setCardIsOpen}>
            <div className="text-lg">
                {locale.enUS.STATEMENTS_PREVIEW.REQUEST_INVITATION_TAB.HEADER}
            </div>
            <div className="mt-4">
                {
                    locale.enUS.STATEMENTS_PREVIEW.REQUEST_INVITATION_TAB
                        .DESCRIPTION
                }
            </div>
            <div className="mt-6 flex items-center justify-between">
                <div className="flex-none w-2/3">
                    <ValidatedInput
                        addClasses="my-3 border border-tertiary-200 text-tertiary-500"
                        inputId={inputIds.requestInvitationEmail}
                        icon="email"
                        type="text"
                        padding="small"
                        placeholder="Email"
                        tooltipPosition="left"
                        value={email}
                        handleChange={setEmail}
                        handleKeyPress={(e: KeyboardEvent<Element>): void => {
                            if (e.key === 'Enter') {
                                handleSubmit();
                            }
                        }}
                        validationOutputs={validationOutputs}
                    />
                </div>
                <div className="flex-grow ml-3">
                    <Button
                        color="secondary-dark-bg"
                        handleClick={handleSubmit}
                        padding="sm"
                    >
                        <div
                            className="text-center"
                            data-cy="request-invitation-button"
                        >
                            {!loading && (
                                <>
                                    {
                                        locale.enUS.STATEMENTS_PREVIEW
                                            .REQUEST_INVITATION_TAB.BUTTONS
                                            .REQUEST
                                    }
                                </>
                            )}
                            {loading && <LoadingEllipses width={40} />}
                        </div>
                    </Button>
                </div>
            </div>
            <div className="text-sm mt-6">
                {
                    locale.enUS.STATEMENTS_PREVIEW.REQUEST_INVITATION_TAB
                        .ALREADY_HAVE_AN_ACCOUNT
                }
                &nbsp;
                <button
                    type="button"
                    className="text-secondary-500 font-semibold underline"
                    onClick={(): void => {
                        history.push(PATHS.SIGN_IN);
                    }}
                >
                    {
                        locale.enUS.STATEMENTS_PREVIEW.REQUEST_INVITATION_TAB
                            .SIGN_IN
                    }
                </button>
            </div>
        </StickyTab>
    );
};
